import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { X, Download, Share2, Plus, HardHat } from "lucide-react";

const DISMISS_KEY = "fl_install_prompt_dismissed_at";
const SNOOZE_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
}
function isAndroid() {
  return /Android/i.test(window.navigator.userAgent || "");
}
function isMobile() {
  return isIOS() || isAndroid() || window.matchMedia("(max-width: 1024px)").matches;
}
function snoozed() {
  const v = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return v && Date.now() - v < SNOOZE_MS;
}

export function InstallPrompt() {
  const { user } = useAuth();
  const [evt, setEvt] = useState(null);     // beforeinstallprompt event (Chrome/Edge/Android)
  const [show, setShow] = useState(false);
  const [iosShow, setIosShow] = useState(false);

  // Only field workers on mobile see the prompt
  const eligible = user?.role === "field" && isMobile() && !isStandalone();

  useEffect(() => {
    if (!eligible) return;
    if (snoozed()) return;

    const onBIP = (e) => {
      e.preventDefault();
      setEvt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS Safari never fires beforeinstallprompt — show manual instructions
    if (isIOS()) {
      const t = setTimeout(() => setIosShow(true), 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onBIP); };
    }

    // If after 8s no event fired and not iOS, give up silently
    const giveup = setTimeout(() => {
      if (!evt) setShow(false);
    }, 8000);

    return () => {
      clearTimeout(giveup);
      window.removeEventListener("beforeinstallprompt", onBIP);
    };
  }, [eligible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onInstalled = () => { setShow(false); setIosShow(false); };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setIosShow(false);
  };

  const install = async () => {
    if (!evt) return;
    evt.prompt();
    try { await evt.userChoice; } catch { /* ignore */ }
    setEvt(null);
    setShow(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!eligible) return null;

  // Native install card (Android Chrome)
  if (show && evt) {
    return (
      <div className="fixed inset-x-3 bottom-[84px] z-50" data-testid="install-prompt-native">
        <div className="bg-[#09090B] text-white border-4 border-[#FF5F15] shadow-[6px_6px_0_0_rgba(0,0,0,0.4)] p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-[#FF5F15] flex items-center justify-center shrink-0">
            <HardHat className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-base uppercase tracking-tight leading-none">
              Install Fieldlink
            </div>
            <div className="text-[11px] text-zinc-400 font-mono uppercase tracking-widest mt-1">
              Add to home screen · works offline · no app store
            </div>
          </div>
          <button
            onClick={install}
            className="bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold uppercase tracking-wider text-xs px-3 py-2 flex items-center gap-1.5"
            data-testid="install-prompt-confirm"
          >
            <Download className="w-3.5 h-3.5" /> Install
          </button>
          <button
            onClick={dismiss}
            className="text-zinc-500 hover:text-white -mr-1"
            aria-label="Dismiss"
            data-testid="install-prompt-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // iOS fallback — manual "Share → Add to Home Screen" instructions
  if (iosShow) {
    return (
      <div className="fixed inset-x-3 bottom-[84px] z-50" data-testid="install-prompt-ios">
        <div className="bg-[#09090B] text-white border-4 border-[#FF5F15] shadow-[6px_6px_0_0_rgba(0,0,0,0.4)] p-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-[#FF5F15] flex items-center justify-center shrink-0">
              <HardHat className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-base uppercase tracking-tight leading-none">
                Install Fieldlink on your iPhone
              </div>
              <ol className="mt-2 space-y-1.5 text-xs text-zinc-200">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[10px] font-bold shrink-0">1</span>
                  <span className="flex items-center gap-1">Tap <Share2 className="w-3.5 h-3.5 inline" /> Share in Safari</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[10px] font-bold shrink-0">2</span>
                  <span className="flex items-center gap-1">Scroll → <Plus className="w-3.5 h-3.5 inline" /> Add to Home Screen</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[10px] font-bold shrink-0">3</span>
                  <span>Tap Add — the orange F appears on your home screen.</span>
                </li>
              </ol>
            </div>
            <button
              onClick={dismiss}
              className="text-zinc-500 hover:text-white -mr-1 -mt-1"
              aria-label="Dismiss"
              data-testid="install-prompt-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={dismiss}
              className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 hover:text-white"
              data-testid="install-prompt-later"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
